import React, { useCallback, useEffect, useState } from "react";
import { ArrowRightLeft, Search } from "lucide-react";
import { supabase } from "@/lib/supabase/supabaseClient";
import { PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { filterPatientsBySearch } from "@/dentlab/src/lib/utils/patient-search-filter";

interface PatientJobsListProps {
	patientId: string | null | undefined;
	// When set, each job can be transferred to another existing patient.
	allowTransfer?: boolean;
	onChanged?: () => void;
}

interface Job {
	id: number;
	title: string | null;
	code: string;
	incoming_order_date: string;
	client_id: string | null;
}

export const PatientJobsList: React.FC<PatientJobsListProps> = ({
	patientId,
	allowTransfer = false,
	onChanged,
}) => {
	const [jobs, setJobs] = useState<Job[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);
	// Job whose transfer panel is open, plus the target search state.
	const [transferJobId, setTransferJobId] = useState<number | null>(null);
	const [targetQuery, setTargetQuery] = useState("");
	const [targetPatient, setTargetPatient] =
		useState<PatientEntityType | null>(null);
	const [isTransferring, setIsTransferring] = useState(false);
	// All org patients as transfer targets, loaded once when a panel opens.
	const [targetCandidates, setTargetCandidates] = useState<
		PatientEntityType[] | null
	>(null);
	const { clientsLookup, organizationId } = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
		organizationId: state.organization?.id,
	}));
	const transferJobToPatient = usePatientStore(
		(state) => state.transferJobToPatient
	);
	const fetchOrgPatientsForDuplicates = usePatientStore(
		(state) => state.fetchOrgPatientsForDuplicates
	);
	const patientName = usePatientStore((state) => state.patientName);

	const loadJobs = useCallback(async () => {
		if (!patientId || !organizationId) {
			setJobs([]);
			setTotalCount(0);
			return;
		}

		setIsLoading(true);
		setHasError(false);

		try {
			const { count, error: countError } = await supabase
				.from("jobs")
				.select("*", { count: "exact", head: true })
				.eq("patient_id", patientId)
				.eq("organization_id", organizationId);

			// Show the patient's jobs, capped for safety
			const { data, error } = await supabase
				.from("jobs")
				.select("id, title, code, incoming_order_date, client_id")
				.eq("patient_id", patientId)
				.eq("organization_id", organizationId)
				.order("incoming_order_date", { ascending: false })
				.limit(100);

			if (error || countError) {
				console.error("Error fetching jobs:", error || countError);
				setHasError(true);
				setJobs([]);
				setTotalCount(0);
			} else {
				setJobs(data || []);
				setTotalCount(count || 0);
			}
		} catch (err) {
			console.error("Error fetching jobs:", err);
			setHasError(true);
		} finally {
			setIsLoading(false);
		}
	}, [patientId, organizationId]);

	useEffect(() => {
		loadJobs();
	}, [loadJobs]);

	const handleOpenTransfer = (jobId: number) => {
		setTransferJobId((current) => (current === jobId ? null : jobId));
		setTargetQuery("");
		setTargetPatient(null);
		if (targetCandidates === null && patientId) {
			fetchOrgPatientsForDuplicates(patientId).then(setTargetCandidates);
		}
	};

	const handleTransfer = async () => {
		if (transferJobId === null || !targetPatient?.id) return;
		setIsTransferring(true);
		const ok = await transferJobToPatient(transferJobId, targetPatient.id);
		setIsTransferring(false);
		if (ok) {
			setTransferJobId(null);
			setTargetPatient(null);
			setTargetQuery("");
			await loadJobs();
			onChanged?.();
		}
	};

	const formatDate = (date: string) => {
		if (!date) return "";
		return new Date(date).toLocaleDateString("de-DE");
	};

	const targetResults =
		targetQuery.trim().length >= 2 && targetCandidates
			? filterPatientsBySearch(targetCandidates, targetQuery)
			: [];

	if (!patientId) {
		return null;
	}

	if (isLoading) {
		return (
			<div>
				<p className="text-sm text-gray-400">Lade Aufträge...</p>
			</div>
		);
	}

	if (hasError) {
		return (
			<div>
				<p className="text-sm text-amber-600">
					Aufträge konnten nicht geladen werden
				</p>
			</div>
		);
	}

	if (totalCount === 0) {
		return (
			<div>
				<p className="text-sm text-gray-400">
					Keine Aufträge vorhanden
				</p>
			</div>
		);
	}

	// Moving the last job away would leave a patient without any job, which
	// must not exist — every patient needs an Auftraggeber and at least one job.
	const isLastJob = totalCount === 1;

	return (
		<div>
			<p className="text-sm font-medium text-gray-700 mb-3">
				{totalCount} {totalCount === 1 ? "Auftrag" : "Aufträge"}{" "}
				insgesamt
			</p>
			<div className="space-y-2">
				{jobs.map((job) => {
					const client = job.client_id
						? clientsLookup[job.client_id]
						: null;
					const clientName = getClientDisplayName(client);
					const isPanelOpen = transferJobId === job.id;

					return (
						<div
							key={job.id}
							className="bg-gray-50 rounded p-3 border border-gray-200"
						>
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<div className="font-medium text-gray-900">
										{job.title || "Auftrag"}
									</div>
									<div className="text-sm text-gray-600 mt-1">
										<span>Code: {job.code}</span>
										{clientName && (
											<span> • Praxis: {clientName}</span>
										)}
										<span>
											{" "}
											• Datum:{" "}
											{formatDate(
												job.incoming_order_date
											)}
										</span>
									</div>
								</div>
								{allowTransfer && (
									<button
										onClick={() =>
											handleOpenTransfer(job.id)
										}
										disabled={isLastJob}
										title={
											isLastJob
												? "Der letzte Auftrag kann nicht verschoben werden – jeder Patient braucht mindestens einen Auftrag."
												: undefined
										}
										className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
										aria-label={`Auftrag ${
											job.title || job.code
										} zu einem anderen Patienten verschieben`}
										data-testid="patient-job-transfer-button"
									>
										<ArrowRightLeft className="w-4 h-4" />
										Verschieben
									</button>
								)}
							</div>

							{isPanelOpen && (
								<div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
									<div className="relative">
										<Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
										<input
											type="text"
											value={targetQuery}
											onChange={(e) => {
												setTargetQuery(e.target.value);
												setTargetPatient(null);
											}}
											placeholder="Zielpatient suchen – Name, Code oder Geburtsdatum"
											className="w-full border border-gray-300 rounded pl-8 pr-3 py-1.5 text-sm"
											autoFocus
											aria-label="Zielpatient suchen"
											data-testid="patient-transfer-target-search"
										/>
									</div>

									{targetCandidates === null ? (
										<p className="text-sm text-gray-400">
											Lade Patienten...
										</p>
									) : targetQuery.trim().length < 2 ? (
										<p className="text-xs text-gray-400">
											Mindestens 2 Zeichen eingeben
										</p>
									) : targetResults.length === 0 ? (
										<p className="text-sm text-gray-500">
											Keine Patienten gefunden
										</p>
									) : (
										<div className="space-y-1 max-h-72 overflow-y-auto pr-1">
											{targetResults.map((patient) => {
												const isSelected =
													targetPatient?.id ===
													patient.id;
												return (
													<button
														key={patient.id}
														type="button"
														onClick={() =>
															setTargetPatient(
																patient
															)
														}
														className={`w-full text-left border rounded px-3 py-2 text-sm ${
															isSelected
																? "border-blue-500 bg-blue-50"
																: "border-gray-200 bg-white hover:border-gray-300"
														}`}
														aria-pressed={
															isSelected
														}
														data-testid="patient-transfer-target-option"
													>
														<span className="font-medium text-gray-900">
															{patientName(
																patient,
																true
															) ||
																"Unbekannter Patient"}
														</span>
														<span className="text-gray-500">
															{patient.code
																? ` • ${patient.code}`
																: ""}
															{patient.birth_date
																? ` • Geb.: ${formatDate(
																		patient.birth_date
																	)}`
																: ""}
															{patient.client_id &&
															getClientDisplayName(
																clientsLookup[
																	patient
																		.client_id
																]
															)
																? ` • ${getClientDisplayName(
																		clientsLookup[
																			patient
																				.client_id
																		]
																	)}`
																: ""}
														</span>
													</button>
												);
											})}
										</div>
									)}

									<div className="flex justify-end gap-2 pt-1">
										<button
											onClick={() =>
												setTransferJobId(null)
											}
											className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
										>
											Abbrechen
										</button>
										<button
											onClick={handleTransfer}
											disabled={
												!targetPatient || isTransferring
											}
											className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
											aria-label="Auftrag zum ausgewählten Patienten verschieben"
											data-testid="patient-transfer-confirm"
										>
											{isTransferring
												? "Wird verschoben..."
												: targetPatient
													? `Zu ${patientName(
															targetPatient,
															true
														)} verschieben`
													: "Auftrag verschieben"}
										</button>
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>
			{totalCount > jobs.length && (
				<p className="text-sm text-gray-400 italic mt-2">
					... und {totalCount - jobs.length} weitere
				</p>
			)}
		</div>
	);
};
